import {
  PeriodEnum,
  type ModelClientFilter,
  type ModelClientsFilterValue,
  type ModelVisitsFilterValue,
} from '@app/services/rtkQuery/Clients/types';

import type { NewClientsListFilters } from '@pages/Clients/NewClientsList/types';

type PreparedFilter = {
  VisitsFilter: ModelVisitsFilterValue;
  ClientsFilter: ModelClientsFilterValue;
  Type: NewClientsListFilters['type'];
};

export const formatClientsFilters = (
  filters: NewClientsListFilters[]
): ModelClientFilter[] => {
  const prepared: PreparedFilter[] = filters.map((filter) => {
    const {
      hasRecords,
      recordsStatuses,
      recordsCountRange,
      recordsTags,
      ageRange,
      soldRange,
      genders,
      clientsTags,
      birthdayPeriod,
      servicesSumRange,
      employees,
      isIncludeClientsTags,
      isIncludeRecordsTags,
      period,
      categories,
      services,
      balanceRange,
      type,
      hasTasks,
      hasLoyaltyCard,
    } = filter;

    const DateStart = period?.[0]?.startOf('day')?.format('YYYY-MM-DD') || null;
    const DateEnd = period?.[1]?.endOf('day')?.format('YYYY-MM-DD') || null;

    const ServiceIds = services
      .filter((service) => !service.startsWith('category-'))
      .map((service) => {
        const parsedId = Number(service.replace('service-', '').trim());
        return Number.isNaN(parsedId) ? null : parsedId;
      })
      .filter((value): value is number => typeof value === 'number');

    const RecordsTagIds =
      recordsTags.length > 0
        ? [!isIncludeRecordsTags ? 0 : null, ...recordsTags].filter(
            (tag): tag is number => tag !== null
          )
        : null;

    const Gender =
      genders.length > 0
        ? genders.map((item) => (item === '' ? null : item))
        : null;

    const formattedVisitFilterValues: ModelVisitsFilterValue = {
      HasRecords: hasRecords,
      Statuses: recordsStatuses.length > 0 ? recordsStatuses : null,
      MinCountRecords: recordsCountRange.from,
      MaxCountRecords: recordsCountRange.to,
      DateStart,
      DateEnd,
      Period: null,
      OffsetDays: null,
      EmployeeIds: employees.length > 0 ? employees : null,
      ServiceCategoryIds: categories.length > 0 ? categories : null,
      ServiceIds: ServiceIds.length > 0 ? ServiceIds : null,
      MinServicesCost: servicesSumRange.from,
      MaxServicesCost: servicesSumRange.to,
      TagIds: RecordsTagIds,
    };

    const ClientsTagIds =
      clientsTags.length > 0
        ? [!isIncludeClientsTags ? 0 : null, ...clientsTags].filter(
            (tag): tag is number => tag !== null
          )
        : null;

    const formattedClientsFilterValues: ModelClientsFilterValue = {
      Gender,
      TagIds: ClientsTagIds,
      MinServicesCost: soldRange.from,
      MaxServicesCost: soldRange.to,
      IsReceivedSMS: null,
      DateStart: birthdayPeriod
        ? birthdayPeriod[0]?.startOf('day')?.format('YYYY-MM-DD') || null
        : null,
      DateEnd: birthdayPeriod
        ? birthdayPeriod[1]?.endOf('day')?.format('YYYY-MM-DD') || null
        : null,
      Birthday: PeriodEnum.Period,
      OffsetDays: null,
      MinAge: ageRange.from,
      MaxAge: ageRange.to,
      BalanceS: balanceRange.from,
      BalanceE: balanceRange.to,
      HasOpenTasks: hasTasks,
      HasLoyaltyCard: hasLoyaltyCard,
    };

    return {
      VisitsFilter: formattedVisitFilterValues,
      ClientsFilter: formattedClientsFilterValues,
      Type: type,
    };
  });

  const result: ModelClientFilter[] = [];

  const createGroup = (
    ClientsFilter: ModelClientsFilterValue,
    VisitsFilter: ModelVisitsFilterValue
  ): ModelClientFilter => ({
    ClientsFilter: [{ IsInclude: true, Values: [ClientsFilter] }],
    VisitsFilter: [{ IsInclude: true, Values: [VisitsFilter] }],
    SalesFilter: [],
  });

  const appendToLastGroup = (
    ClientsFilter: ModelClientsFilterValue,
    VisitsFilter: ModelVisitsFilterValue
  ): void => {
    const lastIndex = result.length - 1;

    if (lastIndex < 0) {
      result.push(createGroup(ClientsFilter, VisitsFilter));
      return;
    }

    result[lastIndex] = {
      ...result[lastIndex],
      ClientsFilter: [
        ...result[lastIndex].ClientsFilter,
        { IsInclude: true, Values: [ClientsFilter] },
      ],
      VisitsFilter: [
        ...result[lastIndex].VisitsFilter,
        { IsInclude: true, Values: [VisitsFilter] },
      ],
    };
  };

  for (const { ClientsFilter, VisitsFilter, Type } of prepared) {
    if (Type === 'and') {
      appendToLastGroup(ClientsFilter, VisitsFilter);
    } else {
      result.push(createGroup(ClientsFilter, VisitsFilter));
    }
  }

  return result;
};
